import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import polygonClipping from 'polygon-clipping';
import { loadGoogleMaps } from '../../utils/googleMaps';
import { clientsService } from '../../services/api';
import {
  measureInfo, soilingFactors, priceShape, fmtMeasure, fmtDim, buildQuoteLines, usesDim, usesDim2, isLengthTimes, dimLabel, dimLabel2, DIM_DEFAULT,
  TRADES, tradeOf, COATS, factorOptions, defaultFactor, defaultCoats
} from '../../utils/measure';
import { formatMoney } from '../../utils/pricing';
import { draftKey, readDraft, writeDraft, clearDraft, shapesSignature, serverTimeIso } from '../../utils/estimatorDraft';
import './EstimatorModal.css';

const GOLD_COAST = { lat: -28.0167, lng: 153.4 };
const MAGS = [1, 1.5, 2, 3, 4];          // magnification steps past the imagery's max zoom
const SNAP_VERTEX_PX = 12;
const SNAP_EDGE_PX = 8;
const HANDLE_PX = 10;
const LINE_HIT_PX = 7;
const EXCLUDE_COLOUR = '#F87171';
const GUIDE_COLOUR = '#FACC15';
let seq = 1;

const AreaIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6l5-2 11 4-2 12-14-3z" /></svg>;
const RectIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="5" width="16" height="14" rx="1" /></svg>;
const LineIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 17l6-6 4 4 8-9" /></svg>;
const CursorIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 3l14 8-6 2-3 6z" /></svg>;
const CutIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6v6H9z" /></svg>;
const EyeIcon = ({ off }) => off
  ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.9 17.9A10 10 0 0 1 2 12a17 17 0 0 1 3.2-4.3M9.9 4.2A10 10 0 0 1 22 12a17 17 0 0 1-2.2 3.2" /><path d="M1 1l22 22" /></svg>
  : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg>;

/**
 * One OverlayView that positions a list of small DOM items (labels, vertex
 * handles, snap ring) on the map. Items counter-scale so they stay the same
 * size on screen while the map is magnified.
 */
function makeDomLayer(maps) {
  return class DomLayer extends maps.OverlayView {
    constructor() { super(); this.items = []; this.root = null; this.scale = 1; }
    onAdd() {
      this.root = document.createElement('div');
      this.root.className = 'est-layer';
      this.getPanes().floatPane.appendChild(this.root);
      this.render();
    }
    onRemove() { this.root?.remove(); this.root = null; }
    setItems(items, scale) {
      this.items = items;
      if (scale) this.scale = scale;
      this.render();
      this.draw();
    }
    render() {
      if (!this.root) return;
      const nodes = this.items.map((it) => {
        const d = document.createElement('div');
        d.className = it.className;
        if (it.text) d.textContent = it.text;
        if (it.colour) d.style.setProperty('--h', it.colour);
        d.style.transform = `translate(-50%, -50%) scale(${1 / this.scale})`;
        return d;
      });
      this.root.replaceChildren(...nodes);
    }
    draw() {
      const proj = this.getProjection();
      if (!proj || !this.root) return;
      this.items.forEach((it, i) => {
        const el = this.root.children[i];
        if (!el || !it.position) return;
        const p = proj.fromLatLngToDivPixel(it.position);
        if (!p) return;
        el.style.left = `${p.x}px`;
        el.style.top = `${p.y + (it.dy || 0) / this.scale}px`; // dy: screen-pixel nudge (e.g. a cut-out label under its area's)
      });
    }
  };
}

function mvcToArray(mvc) {
  const out = [];
  mvc.forEach((x) => out.push(x));
  return out;
}
const pathToLatLngs = mvcToArray;
function round6(n) { return Math.round(n * 1e7) / 1e7; } // 7 decimals ≈ 1 cm — 6 cost ~0.4% on an eave band after a reload
/** Sign tells the winding; Google draws a hole only when it winds opposite to the outer ring. */
function signedArea(pts) {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    s += (b.lng() - a.lng()) * (b.lat() + a.lat());
  }
  return s;
}
function orientHole(outer, hole) {
  return signedArea(outer) * signedArea(hole) > 0 ? [...hole].reverse() : hole;
}
/** Strip a GeoJSON ring's closing point. */
function openRing(coords) {
  if (Array.isArray(coords) && coords.length > 3 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1]) return coords.slice(0, -1);
  return coords;
}

/** A lot boundary (GeoJSON Polygon / MultiPolygon, [lng, lat]) → its outer ring, open. */
function lotRing(geom) {
  if (!geom) return null;
  const poly = geom.type === 'Polygon' ? geom.coordinates
    : geom.type === 'MultiPolygon' ? (geom.coordinates || []).reduce((best, p) => ((p?.[0]?.length || 0) > (best?.[0]?.length || 0) ? p : best), null) : null;
  const ring = openRing(poly?.[0] || []);
  return Array.isArray(ring) && ring.length >= 3 ? ring : null;
}

/** Closest point on segment AB to P, in screen pixels. */
function closestOnSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
  const x = ax + t * dx, y = ay + t * dy;
  return { x, y, t, d: Math.hypot(px - x, py - y) };
}

/**
 * Full-screen map estimator. Google Maps is the renderer (satellite, top-down)
 * and the geometry library; every mouse interaction is ours, through a capture
 * layer over the map. That is what lets us magnify past the imagery's maximum
 * zoom (Google's API stops there; Google's website scales tiles — so do we),
 * snap new points to existing corners and edges, and keep Escape away from the
 * quote modal underneath.
 *
 * Shapes: polygons (with optional inner rings — an eaves band is the outer
 * roof edge minus the inner wall line), lines, and cut-outs.
 *
 * Props: isOpen, onClose, client, surfaces, settings, initial (saved
 * measurements), readOnly, onApply({ items, measurements }),
 * draftId ({ quoteId, customerId } — autosave key), savedAt (quote.updated_at).
 *
 * Autosave (Bug #388): every change is written to localStorage and restored
 * on the next open (or on a hot-reload remount) until the quote is saved.
 */
export default function EstimatorModal({ isOpen, onClose, client, surfaces = [], settings = {}, initial = [], readOnly = false, onApply, draftId = null, savedAt = null, priceLinesDefault = true, groupDefault = false, site = null, variant = 'quote', applyLabel = null, step = null, context = null }) {
  const wrapEl = useRef(null);
  const mapEl = useRef(null);
  const captureEl = useRef(null);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const layersRef = useRef(null);    // { proj, labels, handles, snap }
  const overlaysRef = useRef({});    // key -> { overlay, kind, listeners }
  const draftRef = useRef(null);     // { tool, points, preview, edge, rubber, rect, anchor, colour }
  const innerTargetRef = useRef(null); // shape key receiving an inner outline (tool 'inner')
  const dragRef = useRef(null);      // { type: 'pan'|'vertex', ... }
  const modeRef = useRef('select');
  const magRef = useRef(1);
  const shapesRef = useRef([]);
  const selectedRef = useRef(null);
  const selVertexRef = useRef(null); // { p, i } within the selected shape
  const maxZoomRef = useRef(21);
  const lastClickRef = useRef({ t: 0, x: 0, y: 0 });
  const wheelAccRef = useRef(0);
  const api = useRef({});            // fresh closures for the once-attached DOM listeners
  const bootSigRef = useRef('');     // signature of the shapes as loaded — anything else is unsaved work
  const skipLocalRef = useRef(false); // set by "Discard" so the next boot ignores the autosave
  const isOpenRef = useRef(false);    // read in the boot cleanup: a site change keeps the map, a close tears it down
  isOpenRef.current = isOpen;
  const guideRef = useRef(null);      // the lot boundary drawn as a guide (letterbox drops): snapped to, never priced
  const guideRingRef = useRef([]);    // its open ring (LatLng[]) for snapping + the label
  const contextRef = useRef([]);      // the other lots of a drop (faint) + their traced driveways (teal): orientation, never editable
  const contextLabelsRef = useRef([]);
  const bootKeyRef = useRef(null);    // the autosave key as booted, so a cleanup after a site change flushes under the right key
  const zoomListenerRef = useRef(null);
  const siteKey = site?.id ?? null;   // stepping to another letterbox re-boots on the same map
  const [reloadKey, setReloadKey] = useState(0);
  const [restoredAt, setRestoredAt] = useState(null);

  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');
  const [home, setHome] = useState(null);
  const [mode, setModeState] = useState('select'); // select | polygon | rectangle | line | exclude | inner
  const [shapes, setShapes] = useState([]);        // metadata; geometry lives in overlaysRef
  const [selectedKey, setSelectedKeyState] = useState(null);
  const [selVertex, setSelVertexState] = useState(null);
  const [draftCount, setDraftCount] = useState(-1); // -1 = not drawing
  const [tick, setTick] = useState(0);             // bumps when any path changes
  const [groupBySurface, setGroupBySurface] = useState(false);
  const [priceLines, setPriceLines] = useState(true);
  const [mag, setMagState] = useState(1);
  const [zoom, setZoomState] = useState(20);
  const [dimAreas, setDimAreas] = useState(false);
  const [showCutLabels, setShowCutLabels] = useState(false); // cut-out sizes on the map: off = only the area you work with
  const [cursor, setCursor] = useState('');
  const activeSurfaces = useMemo(() => surfaces.filter((s) => s.is_active), [surfaces]);
  const [nextSurface, setNextSurface] = useState('');
  const [nextSoiling, setNextSoiling] = useState('light'); // washing soiling, or a painting area's condition
  const [trade, setTradeState] = useState('wash');         // Washing / Painting: which surfaces the picker offers (backlog #772)
  const [nextCoats, setNextCoats] = useState(2);
  // Letterbox drops are washing only.
  const shapeSurfaces = useMemo(() => (variant === 'drop' ? activeSurfaces.filter((s) => tradeOf(s) === 'wash') : activeSurfaces), [activeSurfaces, variant]);
  const hasPaint = shapeSurfaces.some((s) => tradeOf(s) === 'paint');

  const factors = useMemo(() => soilingFactors(settings), [settings]);
  const surfaceById = useMemo(() => Object.fromEntries(surfaces.map((s) => [String(s.id), s])), [surfaces]);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => { shapesRef.current = shapes; }, [shapes]);
  const setSelected = useCallback((key) => { selectedRef.current = key; setSelectedKeyState(key); selVertexRef.current = null; setSelVertexState(null); }, []);
  const setSelVertex = useCallback((v) => { selVertexRef.current = v; setSelVertexState(v); }, []);
  const defaultDim = (surface) => (usesDim(surface?.measure) ? (surface.default_height_m ?? DIM_DEFAULT[surface.measure] ?? 0.6) : null);
  const defaultDim2 = (surface) => (usesDim2(surface?.measure) ? (surface.default_dim2_m ?? 0.1) : null);
  // The surface picker can hold "primary+companion" (Eaves + Gutters): both are drawn from one outline.
  const primaryOf = (value) => surfaceById[String(value || '').split('+')[0]] || null;
  const companionOf = (value) => { const id = String(value || '').split('+')[1]; return id ? surfaceById[id] || null : null; };
  const pickerOptions = useMemo(() => {
    const opts = [];
    for (const s of shapeSurfaces.filter((x) => tradeOf(x) === trade)) {
      opts.push({ value: String(s.id), label: s.name });
      const c = s.companion_surface_id ? surfaceById[String(s.companion_surface_id)] : null;
      if (c && c.is_active) opts.push({ value: `${s.id}+${c.id}`, label: `${s.name} + ${c.name}` });
    }
    return opts;
  }, [shapeSurfaces, surfaceById, trade]);
  function setTrade(t) {
    setTradeState(t);
    const first = shapeSurfaces.find((s) => tradeOf(s) === t);
    setNextSurface(first ? String(first.id) : '');
    setNextSoiling(defaultFactor(t));
  }
  const shapeTrade = (s) => (s?.coats != null ? 'paint' : 'wash'); // coats are set on painting areas only
  /** Soiling (washing) or condition + coats (painting) for a shape on `surface`, carried over from `from` when the trade matches. */
  const factorFields = (surface, from) => {
    const t = tradeOf(surface);
    const same = from?.trade === t;
    return { soiling: same ? from.soiling : defaultFactor(t), coats: t === 'paint' ? ((same && from.coats) || defaultCoats(settings)) : null };
  };
  /** Surface <option>s, grouped Washing / Painting once there are painting surfaces. */
  const surfaceOptions = (list) => (hasPaint
    ? TRADES.map((t) => {
      const group = list.filter((s) => tradeOf(s) === t.value);
      return group.length ? <optgroup key={t.value} label={t.label}>{group.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup> : null;
    })
    : list.map((s) => <option key={s.id} value={s.id}>{s.name}</option>));

  // ---------- screen <-> map ----------
  const proj = () => layersRef.current?.proj?.getProjection() || null;
  const wrapSize = () => ({ W: wrapEl.current?.clientWidth || 1, H: wrapEl.current?.clientHeight || 1 });
  const eventPos = (e) => { const r = wrapEl.current.getBoundingClientRect(); return { sx: e.clientX - r.left, sy: e.clientY - r.top }; };
  const toLatLng = (sx, sy) => { const p = proj(); if (!p) return null; return p.fromContainerPixelToLatLng(new mapsRef.current.Point(sx / magRef.current, sy / magRef.current)); };
  const toScreen = (ll) => { const p = proj(); if (!p) return null; const q = p.fromLatLngToContainerPixel(ll); return { x: q.x * magRef.current, y: q.y * magRef.current }; };

  /** Map centre that puts `ll` under screen point (sx, sy) at zoom z and magnification S. */
  const centreFor = (ll, sx, sy, z, S) => {
    const maps = mapsRef.current; const map = mapRef.current;
    const P = map.getProjection();
    const { W, H } = wrapSize();
    const scale = 2 ** z;
    const w = P.fromLatLngToPoint(ll);
    const cx = w.x * scale - (sx / S - W / (2 * S));
    const cy = w.y * scale - (sy / S - H / (2 * S));
    return P.fromPointToLatLng(new maps.Point(cx / scale, cy / scale));
  };

  const applyMag = (S, sx = null, sy = null) => {
    const maps = mapsRef.current; const map = mapRef.current;
    if (!map || !mapEl.current) return;
    const { W, H } = wrapSize();
    const anchor = sx !== null ? toLatLng(sx, sy) : map.getCenter();
    const ax = sx !== null ? sx : W / 2, ay = sy !== null ? sy : H / 2;
    magRef.current = S;
    setMagState(S);
    mapEl.current.dataset.estMag = String(S); // read by the bounding-rect shim in utils/googleMaps.js
    mapEl.current.style.setProperty('--est-inv', String(1 / S)); // keeps Google's logo + attribution their normal size
    mapEl.current.style.width = `${W / S}px`;
    mapEl.current.style.height = `${H / S}px`;
    mapEl.current.style.transform = `scale(${S})`;
    maps.event.trigger(map, 'resize');
    map.setCenter(centreFor(anchor, ax, ay, map.getZoom(), S));
    for (const layer of ['labels', 'handles', 'snap']) layersRef.current?.[layer]?.setItems(layersRef.current[layer].items, S);
    // Strokes are scaled with the map — keep them a sane width on screen.
    for (const o of Object.values(overlaysRef.current)) o.overlay.setOptions({ strokeWeight: Math.max(1.2, 3 / S) });
  };

  const zoomAt = (sx, sy, dir) => {
    const map = mapRef.current; if (!map) return;
    const S = magRef.current;
    const z = map.getZoom();
    const { W, H } = wrapSize();
    const ax = sx ?? W / 2, ay = sy ?? H / 2;
    if (dir > 0) {
      if (z < maxZoomRef.current) {
        const ll = toLatLng(ax, ay);
        map.moveCamera({ zoom: z + 1, center: centreFor(ll, ax, ay, z + 1, S) });
        if (map.getZoom() <= z) { maxZoomRef.current = z; zoomAt(sx, sy, dir); return; } // imagery ran out sooner than advertised
        setZoomState(map.getZoom());
      } else {
        const i = MAGS.indexOf(S);
        if (i < MAGS.length - 1) applyMag(MAGS[i + 1], ax, ay);
      }
    } else if (S > 1) {
      const i = MAGS.indexOf(S);
      applyMag(MAGS[Math.max(0, i - 1)], ax, ay);
    } else if (z > 3) {
      const ll = toLatLng(ax, ay);
      map.moveCamera({ zoom: z - 1, center: centreFor(ll, ax, ay, z - 1, S) });
      setZoomState(map.getZoom());
    }
  };

  const setMode = useCallback((m) => {
    if (draftRef.current) cancelDraft();
    modeRef.current = m;
    setModeState(m);
    setSelected(null); // no edit handles in the way while drawing (Bug #384)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSelected]);

  // ---------- geometry ----------
  /** Every path of a shape: a polygon's outer ring + inner rings, or a line's single path. */
  const pathsOf = (entry) => (entry.kind === 'polygon' ? mvcToArray(entry.overlay.getPaths()) : [entry.overlay.getPath()]);
  /** Closed GeoJSON-style rings ([lng, lat]) for every path with 3+ points. */
  const ringsOf = (entry) => pathsOf(entry).map((path) => {
    const pts = pathToLatLngs(path).map((p) => [p.lng(), p.lat()]);
    if (pts.length) pts.push(pts[0]);
    return pts;
  }).filter((r) => r.length >= 4);
  // Cut-out rings from the given shape list (the live React list, not the ref —
  // the ref syncs after render, which made the subtraction lag one frame).
  const exclusionEntries = (list) => list
    .filter((s) => s.role === 'exclude' && overlaysRef.current[s.key])
    .map((s) => ({ key: s.key, ring: ringsOf(overlaysRef.current[s.key])[0] }))
    .filter((e) => e.ring && e.ring.length >= 4);
  const exclusionRings = (list) => exclusionEntries(list).map((e) => e.ring);

  const areaOfRing = (ring) => {
    const maps = mapsRef.current;
    return maps.geometry.spherical.computeArea(ring.map(([lng, lat]) => new maps.LatLng(lat, lng)));
  };
  const perimeterOf = (path) => {
    const sph = mapsRef.current.geometry.spherical;
    const pts = pathToLatLngs(path);
    return sph.computeLength(path) + (pts.length > 1 ? sph.computeDistanceBetween(pts[pts.length - 1], pts[0]) : 0);
  };

  /**
   * Measure one shape for a given surface measure. `dim` is the shape's height
   * or width (used by ring as the perimeter × width fallback).
   * Returns { raw, gross, perimeter, hasInner, estimated, noInner, invalid }.
   */
  const measureOf = useCallback((key, measure, role, list, dim = 0) => {
    const maps = mapsRef.current;
    const entry = overlaysRef.current[key];
    const none = { raw: 0, gross: 0, perimeter: 0, hasInner: false, estimated: false, noInner: false, invalid: false, cutParts: [] };
    if (!maps || !entry) return none;
    const sph = maps.geometry.spherical;
    if (entry.kind === 'polygon') {
      const paths = pathsOf(entry);
      const outer = paths[0];
      const rings = ringsOf(entry);
      if (!rings.length) return none;
      const gross = sph.computeArea(outer);
      const perimeter = perimeterOf(outer);
      const hasInner = rings.length > 1;
      if (role === 'exclude') return { ...none, raw: gross, gross, perimeter };
      if (measure === 'area' || measure === 'ring') {
        if (measure === 'ring' && !hasInner) {
          if (dim > 0) return { ...none, raw: perimeter * dim, gross, perimeter, estimated: true };
          return { ...none, raw: gross, gross, perimeter, noInner: true };
        }
        // Net of inner rings (eaves: the wall line) and of any cut-outs (pool, garden bed).
        const cutEntries = exclusionEntries(list || shapesRef.current);
        const cuts = cutEntries.map((e) => e.ring);
        if (!hasInner && !cuts.length) return { ...none, raw: gross, gross, perimeter };
        const areaOfResult = (result) => {
          let a = 0;
          for (const poly of result) { a += areaOfRing(poly[0]); for (const hole of poly.slice(1)) a -= areaOfRing(hole); }
          return a;
        };
        let net = gross;
        const cutParts = [];
        try {
          const subject = [rings];
          const result = cuts.length ? polygonClipping.difference(subject, ...cuts.map((r) => [r])) : polygonClipping.union(subject);
          net = areaOfResult(result);
          // How much each cut-out takes from THIS area (so the panel can nest it underneath).
          for (const e of cutEntries) {
            const part = areaOfResult(polygonClipping.intersection(subject, [e.ring]));
            if (part > 0.05) cutParts.push({ key: e.key, area: part });
          }
        } catch { net = gross; }
        return { ...none, raw: Math.max(0, net), gross, perimeter, hasInner, cutParts };
      }
      // A closed outline priced per lineal metre = its perimeter (roof outline → gutters).
      return { ...none, raw: perimeter, gross: perimeter, perimeter };
    }
    if (measure === 'area' || measure === 'ring') return { ...none, invalid: true };
    const len = sph.computeLength(entry.overlay.getPath());
    return { ...none, raw: len, gross: len, perimeter: len };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labelPosition = (entry) => {
    const maps = mapsRef.current;
    const pts = pathToLatLngs(pathsOf(entry)[0]);
    if (!pts.length) return null;
    if (entry.kind === 'polygon') {
      let lat = 0, lng = 0;
      for (const p of pts) { lat += p.lat(); lng += p.lng(); }
      return new maps.LatLng(lat / pts.length, lng / pts.length);
    }
    if (pts.length === 1) return pts[0];
    const i = Math.floor((pts.length - 1) / 2);
    return maps.geometry.spherical.interpolate(pts[i], pts[i + 1], 0.5);
  };

  const geojsonOf = (entry) => {
    if (entry.kind === 'polygon') {
      const rings = pathsOf(entry).map((path) => {
        const pts = pathToLatLngs(path).map((p) => [round6(p.lng()), round6(p.lat())]);
        return pts.length ? [...pts, pts[0]] : pts;
      }).filter((r) => r.length >= 4);
      return JSON.stringify({ type: 'Polygon', coordinates: rings });
    }
    const pts = pathToLatLngs(entry.overlay.getPath()).map((p) => [round6(p.lng()), round6(p.lat())]);
    return JSON.stringify({ type: 'LineString', coordinates: pts });
  };

  /** The current shapes as plain data (what the autosave stores and Apply sends). */
  const snapshotShapes = (list = shapesRef.current) => list
    .filter((s) => overlaysRef.current[s.key])
    .map((s) => ({
      kind: s.kind, role: s.role || 'surface', label: s.label, surface_rate_id: s.surface_rate_id || '',
      soiling: s.soiling || 'light', coats: s.coats ?? null, height_m: s.height_m ?? null, width2_m: s.width2_m ?? null, hidden: !!s.hidden,
      surface_name: s.surface_name, saved_rate: s.saved_rate, saved_measure: s.saved_measure,
      geojson: geojsonOf(overlaysRef.current[s.key])
    }));
  const currentKey = () => bootKeyRef.current || draftKey(draftId || {});
  const flushDraft = () => {
    if (readOnly || status !== 'ready' || skipLocalRef.current) return; // skipLocal = a Discard is in progress
    const key = currentKey(); if (!key) return;
    const snap = snapshotShapes();
    if (shapesSignature(snap) === bootSigRef.current) return; // nothing unsaved
    writeDraft(key, snap);
  };

  // ---------- overlays ----------
  const attachPathListeners = (entry, path) => {
    for (const ev of ['set_at', 'insert_at', 'remove_at']) entry.listeners.push(path.addListener(ev, bump));
  };
  const removeOverlay = useCallback((key) => {
    const entry = overlaysRef.current[key];
    if (!entry) return;
    entry.listeners.forEach((l) => l.remove());
    entry.overlay.setMap(null);
    delete overlaysRef.current[key];
  }, []);

  /** rings: for a polygon [outer, ...inner] as LatLng arrays; for a line [points]. */
  const addOverlay = useCallback((key, kind, rings, colour) => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    const common = { map, strokeColor: colour, strokeOpacity: 0.95, strokeWeight: Math.max(1.2, 3 / magRef.current), zIndex: 10, clickable: false };
    let overlay;
    if (kind === 'polygon') {
      const outer = rings[0] || [];
      const holes = rings.slice(1).filter((h) => h.length >= 3).map((h) => orientHole(outer, h));
      overlay = new maps.Polygon({ ...common, paths: [outer, ...holes], fillColor: colour, fillOpacity: 0.3 });
    } else {
      overlay = new maps.Polyline({ ...common, path: rings[0] || [] });
    }
    const entry = { overlay, listeners: [], kind };
    overlaysRef.current[key] = entry;
    for (const path of pathsOf(entry)) attachPathListeners(entry, path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bump]);

  // ---------- snapping + hit testing ----------
  /** A Line that ends back on its first corner (gutters right round a house, Bug #479). */
  const isClosedRun = (entry) => {
    if (entry?.kind !== 'line') return false;
    const pts = pathToLatLngs(entry.overlay.getPath());
    return pts.length >= 4 && pts[0].equals(pts[pts.length - 1]);
  };
  /** The other end of a closed run's joint — both ends move as one corner. */
  const twinEnd = (entry, i) => {
    if (!isClosedRun(entry)) return null;
    const last = entry.overlay.getPath().getLength() - 1;
    return i === 0 ? last : i === last ? 0 : null;
  };

  const findSnap = (sx, sy, { excludeKey = null, excludePath = null, excludeVertex = null, excludeTwin = null, includeDraftFirst = false } = {}) => {
    let best = null;
    const consider = (ll, kind, tol) => {
      const s = toScreen(ll); if (!s) return;
      const d = Math.hypot(s.x - sx, s.y - sy);
      if (d <= tol && (!best || d < best.d)) best = { latLng: ll, kind, d };
    };
    const excluded = (key, p, i) => key === excludeKey && p === excludePath && (i === excludeVertex || i === excludeTwin);
    const visible = shapesRef.current.filter((s) => !s.hidden && overlaysRef.current[s.key]);
    for (const sh of visible) {
      pathsOf(overlaysRef.current[sh.key]).forEach((path, p) => {
        pathToLatLngs(path).forEach((ll, i) => { if (!excluded(sh.key, p, i)) consider(ll, 'vertex', SNAP_VERTEX_PX); });
      });
    }
    const guidePts = []; // the lot line only shows which house is chosen — driveways run over it to the kerb, so it is not snapped to (Andrew, GID-019)
    if (includeDraftFirst && draftRef.current?.points?.length >= 3) consider(draftRef.current.points[0], 'close', SNAP_VERTEX_PX);
    if (best) return best;
    for (const sh of visible) {
      const entry = overlaysRef.current[sh.key];
      pathsOf(entry).forEach((path, p) => {
        const pts = pathToLatLngs(path);
        const n = pts.length;
        const segs = entry.kind === 'polygon' ? n : n - 1;
        for (let i = 0; i < segs; i++) {
          const j = (i + 1) % n;
          if (excluded(sh.key, p, i) || excluded(sh.key, p, j)) continue;
          const A = toScreen(pts[i]), B = toScreen(pts[j]);
          if (!A || !B) continue;
          const c = closestOnSegment(sx, sy, A.x, A.y, B.x, B.y);
          if (c.d <= SNAP_EDGE_PX && (!best || c.d < best.d)) best = { latLng: toLatLng(c.x, c.y), kind: 'edge', d: c.d };
        }
      });
    }
    for (let i = 0; i < guidePts.length; i++) { // the lot boundary (a driveway usually meets it at the street)
      const A = toScreen(guidePts[i]), B = toScreen(guidePts[(i + 1) % guidePts.length]);
      if (!A || !B) continue;
      const c = closestOnSegment(sx, sy, A.x, A.y, B.x, B.y);
      if (c.d <= SNAP_EDGE_PX && (!best || c.d < best.d)) best = { latLng: toLatLng(c.x, c.y), kind: 'edge', d: c.d };
    }
    return best;
  };

  const distToOutline = (sx, sy, entry) => {
    let best = Infinity;
    for (const path of pathsOf(entry)) {
      const pts = pathToLatLngs(path);
      const n = pts.length;
      const segs = entry.kind === 'polygon' ? n : n - 1;
      for (let i = 0; i < segs; i++) {
        const A = toScreen(pts[i]), B = toScreen(pts[(i + 1) % n]);
        if (!A || !B) continue;
        best = Math.min(best, closestOnSegment(sx, sy, A.x, A.y, B.x, B.y).d);
      }
    }
    return best;
  };

  const hitShape = (sx, sy) => {
    const maps = mapsRef.current;
    const ll = toLatLng(sx, sy);
    const visible = shapesRef.current.filter((s) => !s.hidden && overlaysRef.current[s.key]).reverse();
    for (const sh of visible) {
      const entry = overlaysRef.current[sh.key];
      if (entry.kind === 'line' && distToOutline(sx, sy, entry) <= LINE_HIT_PX) return sh.key;
    }
    for (const sh of visible) {
      const entry = overlaysRef.current[sh.key];
      if (entry.kind !== 'polygon') continue;
      if (distToOutline(sx, sy, entry) <= LINE_HIT_PX) return sh.key;
      if (ll && maps.geometry.poly.containsLocation(ll, entry.overlay)) return sh.key;
    }
    return null;
  };

  const hitHandle = (sx, sy) => {
    const key = selectedRef.current;
    const entry = key && overlaysRef.current[key];
    if (!entry) return null;
    const paths = pathsOf(entry);
    for (let p = 0; p < paths.length; p++) {
      const pts = pathToLatLngs(paths[p]);
      for (let i = 0; i < pts.length; i++) {
        const s = toScreen(pts[i]);
        if (s && Math.hypot(s.x - sx, s.y - sy) <= HANDLE_PX) return { type: 'vertex', p, i };
      }
    }
    for (let p = 0; p < paths.length; p++) {
      const pts = pathToLatLngs(paths[p]);
      const n = pts.length;
      const segs = entry.kind === 'polygon' ? n : n - 1;
      for (let i = 0; i < segs; i++) {
        const A = toScreen(pts[i]), B = toScreen(pts[(i + 1) % n]);
        if (!A || !B) continue;
        const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2;
        if (Math.hypot(mx - sx, my - sy) <= HANDLE_PX) return { type: 'mid', p, i };
      }
    }
    return null;
  };

  const showSnap = (snap) => {
    layersRef.current?.snap?.setItems(snap ? [{ position: snap.latLng, className: `est-snap ${snap.kind}` }] : [], magRef.current);
  };

  // ---------- drafting ----------
  const clearDraftPreview = () => {
    const d = draftRef.current;
    if (!d) return;
    d.preview?.setMap(null); d.edge?.setMap(null); d.rubber?.setMap(null); d.rect?.setMap(null);
  };
  function cancelDraft() {
    clearDraftPreview();
    draftRef.current = null;
    setDraftCount(-1);
    showSnap(null);
    layersRef.current?.handles?.setItems([], magRef.current);
    if (modeRef.current === 'inner') { modeRef.current = 'select'; setModeState('select'); innerTargetRef.current = null; }
  }

  const draftColour = () => {
    const m = modeRef.current;
    if (m === 'exclude') return EXCLUDE_COLOUR;
    if (m === 'inner') {
      const target = shapesRef.current.find((s) => s.key === innerTargetRef.current);
      return surfaceById[target?.surface_rate_id]?.colour || '#2DD4BF';
    }
    const surface = primaryOf(nextSurface) || activeSurfaces[0];
    return surface?.colour || '#2DD4BF';
  };
  const isOutlineTool = (tool) => tool === 'polygon' || tool === 'exclude' || tool === 'inner';

  const ensureDraft = () => {
    if (draftRef.current) return draftRef.current;
    const maps = mapsRef.current; const map = mapRef.current;
    const tool = modeRef.current;
    const colour = draftColour();
    const sw = Math.max(1.2, 3 / magRef.current);
    const d = { tool, points: [], preview: null, edge: null, rubber: null, rect: null, anchor: null, colour };
    if (isOutlineTool(tool)) {
      d.preview = new maps.Polygon({ map, paths: [], fillColor: tool === 'exclude' ? '#000' : colour, fillOpacity: tool === 'inner' ? 0.12 : 0.2, strokeOpacity: 0, clickable: false, zIndex: 20 });
      d.edge = new maps.Polyline({ map, path: [], strokeColor: colour, strokeWeight: sw, clickable: false, zIndex: 21 });
    } else if (tool === 'line') {
      d.edge = new maps.Polyline({ map, path: [], strokeColor: colour, strokeWeight: sw, clickable: false, zIndex: 21 });
    }
    d.rubber = new maps.Polyline({ map, path: [], strokeColor: colour, strokeOpacity: 0.6, strokeWeight: Math.max(1, 2 / magRef.current), clickable: false, zIndex: 22 });
    draftRef.current = d;
    return d;
  };

  const renderDraftDots = () => {
    const d = draftRef.current;
    layersRef.current?.handles?.setItems(d && d.tool !== 'rectangle' ? d.points.map((p) => ({ position: p, className: 'est-dot', colour: d.colour })) : [], magRef.current);
  };

  const addDraftPoint = (ll) => {
    const maps = mapsRef.current; const map = mapRef.current;
    const d = ensureDraft();
    if (d.tool === 'rectangle') {
      if (!d.anchor) {
        d.anchor = ll;
        d.rect = new maps.Rectangle({ map, bounds: new maps.LatLngBounds(ll, ll), fillColor: d.colour, fillOpacity: 0.2, strokeColor: d.colour, strokeWeight: Math.max(1.2, 3 / magRef.current), clickable: false, zIndex: 20 });
        setDraftCount(1);
      } else {
        d.rect.setBounds(new maps.LatLngBounds(d.anchor, d.anchor).extend(ll));
        finishDraft();
      }
      return;
    }
    d.points.push(ll);
    d.preview?.setPath(d.points);
    d.edge?.setPath(d.points);
    renderDraftDots();
    setDraftCount(d.points.length);
  };

  const updateRubber = (ll) => {
    const d = draftRef.current; const maps = mapsRef.current;
    if (!d || !ll) return;
    if (d.tool === 'rectangle') {
      if (d.anchor && d.rect) d.rect.setBounds(new maps.LatLngBounds(d.anchor, d.anchor).extend(ll));
      return;
    }
    if (!d.points.length) return;
    const last = d.points[d.points.length - 1];
    d.rubber?.setPath(isOutlineTool(d.tool) && d.points.length > 1 ? [last, ll, d.points[0]] : [last, ll]);
  };

  /** Start drawing the inner outline (eaves: the wall line) of an existing polygon. */
  function startInnerDraft(key) {
    if (draftRef.current) cancelDraft();
    innerTargetRef.current = key;
    modeRef.current = 'inner';
    setModeState('inner');
    setSelected(null);
  }
  function removeInner(key) {
    const entry = overlaysRef.current[key];
    if (!entry || entry.kind !== 'polygon') return;
    const paths = entry.overlay.getPaths();
    while (paths.getLength() > 1) paths.removeAt(paths.getLength() - 1);
    bump();
  }

  function finishDraft() {
    const d = draftRef.current;
    if (!d) return;
    const maps = mapsRef.current;
    let pts = d.points;

    if (d.tool === 'inner') {
      const key = innerTargetRef.current;
      const entry = key && overlaysRef.current[key];
      if (!entry || entry.kind !== 'polygon' || pts.length < 3) { cancelDraft(); return; }
      const paths = entry.overlay.getPaths();
      const outer = pathToLatLngs(paths.getAt(0));
      while (paths.getLength() > 1) paths.removeAt(paths.getLength() - 1);
      const hole = new maps.MVCArray(orientHole(outer, pts));
      paths.push(hole);
      attachPathListeners(entry, hole);
      cancelDraft();
      setSelected(key);
      bump();
      return;
    }

    const kind = d.tool === 'line' ? 'line' : 'polygon';
    if (d.tool === 'rectangle') {
      if (!d.rect) { cancelDraft(); return; }
      const b = d.rect.getBounds();
      const ne = b.getNorthEast(); const sw = b.getSouthWest();
      pts = [ne, new maps.LatLng(ne.lat(), sw.lng()), sw, new maps.LatLng(sw.lat(), ne.lng())];
    }
    const need = kind === 'polygon' ? 3 : 2;
    if (pts.length < need) { cancelDraft(); return; }
    const isCut = d.tool === 'exclude';
    const surface = isCut ? null : (primaryOf(nextSurface) || activeSurfaces[0] || null);
    const companion = isCut ? null : companionOf(nextSurface);
    const key = `s${seq++}`;
    const colour = isCut ? EXCLUDE_COLOUR : (surface?.colour || '#2DD4BF');
    const from = { trade, soiling: nextSoiling, coats: nextCoats };
    cancelDraft();
    addOverlay(key, kind, [pts], colour);
    const created = [{
      key, kind, role: isCut ? 'exclude' : 'surface', hidden: false,
      label: isCut ? 'Cut-out' : (surface?.name || (kind === 'line' ? 'Run' : 'Area')),
      surface_rate_id: surface ? String(surface.id) : '',
      ...(isCut ? { soiling: 'light', coats: null } : factorFields(surface, from)),
      height_m: defaultDim(surface),
      width2_m: defaultDim2(surface)
    }];
    if (companion) {
      // "Eaves + Gutters": the same outline also becomes the companion surface's shape.
      const key2 = `s${seq++}`;
      addOverlay(key2, kind, [pts.slice()], companion.colour || '#2DD4BF');
      created.push({
        key: key2, kind, role: 'surface', hidden: false, label: companion.name, surface_rate_id: String(companion.id),
        ...factorFields(companion, from), height_m: defaultDim(companion), width2_m: defaultDim2(companion)
      });
    }
    setShapes((prev) => [...prev, ...created]);
    bump();
    if (kind === 'polygon' && surface?.measure === 'ring') {
      startInnerDraft(key); // eaves: straight on to the inner edge
    } else {
      modeRef.current = 'select';
      setModeState('select');
      setSelected(key);
    }
  }

  const popDraft = () => {
    const d = draftRef.current;
    if (!d || d.tool === 'rectangle') return;
    d.points.pop();
    if (!d.points.length) { cancelDraft(); return; }
    d.preview?.setPath(d.points); d.edge?.setPath(d.points);
    renderDraftDots();
    setDraftCount(d.points.length);
  };

  // ---------- pointer interaction (capture layer) ----------
  const panByScreen = (dx, dy) => {
    const map = mapRef.current; if (!map) return;
    const { W, H } = wrapSize();
    map.setCenter(toLatLng(W / 2 - dx, H / 2 - dy) || map.getCenter());
  };

  const handleClick = (sx, sy) => {
    const now = Date.now();
    const lc = lastClickRef.current;
    const isDouble = now - lc.t < 350 && Math.hypot(sx - lc.x, sy - lc.y) < 6;
    lastClickRef.current = { t: now, x: sx, y: sy };
    const m = modeRef.current;
    if (readOnly) return;
    if (m === 'select') {
      if (isDouble) return;
      setSelected(hitShape(sx, sy));
      return;
    }
    if (isDouble) { finishDraft(); return; }
    const snap = findSnap(sx, sy, { includeDraftFirst: true });
    if (snap?.kind === 'close') {
      // A polygon closes itself; a Line needs the first corner again or its last leg is lost (Bug #479).
      if (draftRef.current?.tool === 'line') addDraftPoint(draftRef.current.points[0]);
      finishDraft(); return;
    }
    const ll = snap?.latLng || toLatLng(sx, sy);
    if (ll) addDraftPoint(ll);
  };

  api.current = {
    onPointerDown(e) {
      if (e.button !== 0 || status !== 'ready') return;
      captureEl.current.setPointerCapture?.(e.pointerId);
      const { sx, sy } = eventPos(e);
      if (!readOnly && modeRef.current === 'select' && selectedRef.current) {
        let h = hitHandle(sx, sy);
        if (h) {
          const entry = overlaysRef.current[selectedRef.current];
          const path = pathsOf(entry)[h.p];
          if (h.type === 'mid') {
            const pts = pathToLatLngs(path);
            const j = (h.i + 1) % pts.length;
            path.insertAt(h.i + 1, mapsRef.current.geometry.spherical.interpolate(pts[h.i], pts[j], 0.5));
            h = { type: 'vertex', p: h.p, i: h.i + 1 };
          }
          dragRef.current = { type: 'vertex', key: selectedRef.current, p: h.p, i: h.i, twin: twinEnd(entry, h.i), x0: sx, y0: sy, moved: false };
          setSelVertex({ p: h.p, i: h.i });
          setCursor('vertex');
          return;
        }
      }
      dragRef.current = { type: 'pan', x0: sx, y0: sy, lx: sx, ly: sy, moved: false };
    },
    onPointerMove(e) {
      if (status !== 'ready') return;
      const { sx, sy } = eventPos(e);
      const d = dragRef.current;
      if (d) {
        if (!d.moved && Math.hypot(sx - d.x0, sy - d.y0) < 4) return;
        d.moved = true;
        if (d.type === 'pan') {
          panByScreen(sx - d.lx, sy - d.ly);
          d.lx = sx; d.ly = sy;
          setCursor('panning');
        } else if (d.type === 'vertex') {
          const snap = findSnap(sx, sy, { excludeKey: d.key, excludePath: d.p, excludeVertex: d.i, excludeTwin: d.twin });
          const ll = snap?.latLng || toLatLng(sx, sy);
          const entry = overlaysRef.current[d.key];
          const path = entry && pathsOf(entry)[d.p];
          if (ll && path) { path.setAt(d.i, ll); if (d.twin != null) path.setAt(d.twin, ll); }
          showSnap(snap);
        }
        return;
      }
      if (readOnly) return;
      if (modeRef.current !== 'select') {
        const snap = findSnap(sx, sy, { includeDraftFirst: true });
        showSnap(snap);
        updateRubber(snap?.latLng || toLatLng(sx, sy));
      } else if (selectedRef.current && hitHandle(sx, sy)) {
        setCursor('vertex');
      } else {
        setCursor('');
      }
    },
    onPointerUp(e) {
      const d = dragRef.current;
      dragRef.current = null;
      setCursor('');
      if (!d || status !== 'ready') return;
      const { sx, sy } = eventPos(e);
      if (d.type === 'vertex') { showSnap(null); bump(); return; }
      if (d.moved) return;
      handleClick(sx, sy);
    },
    onPointerCancel() { dragRef.current = null; setCursor(''); },
    onWheel(e) {
      e.preventDefault();
      if (status !== 'ready') return;
      wheelAccRef.current += e.deltaY;
      if (Math.abs(wheelAccRef.current) < 40) return;
      const dir = wheelAccRef.current < 0 ? 1 : -1;
      wheelAccRef.current = 0;
      const { sx, sy } = eventPos(e);
      zoomAt(sx, sy, dir);
    },
    onContextMenu(e) { e.preventDefault(); },
    // The keyboard effect is attached once; these keep it on fresh closures.
    finishDraft, popDraft, cancelDraft, zoomAt, removeShape, flushDraft
  };

  // ---------- map boot ----------
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setStatus('loading'); setError(''); setBanner(''); setHome(null);
    setShapes([]); shapesRef.current = [];
    setSelected(null); setDraftCount(-1); setGroupBySurface(!!groupDefault); setDimAreas(false); setShowCutLabels(false); setPriceLines(priceLinesDefault !== false);
    modeRef.current = 'select'; setModeState('select'); innerTargetRef.current = null;
    magRef.current = 1; setMagState(1); maxZoomRef.current = 21;
    overlaysRef.current = {}; draftRef.current = null; dragRef.current = null;
    const firstSurface = shapeSurfaces.find((s) => tradeOf(s) === 'wash') || shapeSurfaces[0];
    setTradeState(tradeOf(firstSurface));
    setNextSurface(firstSurface ? String(firstSurface.id) : '');
    setNextSoiling(defaultFactor(tradeOf(firstSurface)));
    setNextCoats(defaultCoats(settings));
    bootKeyRef.current = draftKey(draftId || {});

    const onAuthFail = () => { if (!cancelled) { setStatus('error'); setError('Google rejected the Maps key. In Google Cloud, check the key is restricted to this site\'s address and that the Maps JavaScript API is enabled.'); } };
    window.addEventListener('gid-maps-auth-failed', onAuthFail);

    const cap = captureEl.current;
    const h = {
      pointerdown: (e) => api.current.onPointerDown(e),
      pointermove: (e) => api.current.onPointerMove(e),
      pointerup: (e) => api.current.onPointerUp(e),
      pointercancel: (e) => api.current.onPointerCancel(e),
      wheel: (e) => api.current.onWheel(e),
      contextmenu: (e) => api.current.onContextMenu(e)
    };
    for (const [ev, fn] of Object.entries(h)) cap?.addEventListener(ev, fn, ev === 'wheel' ? { passive: false } : undefined);
    const onResize = () => { if (mapRef.current) { const c = mapRef.current.getCenter(); applyMag(magRef.current); mapRef.current.setCenter(c); } };
    window.addEventListener('resize', onResize);

    (async () => {
      try {
        const maps = await loadGoogleMaps();
        if (cancelled || !mapEl.current) return;
        mapsRef.current = maps;
        const DomLayer = makeDomLayer(maps);

        // Where to open: the site (a letterbox in a drop), else the client's cached pin, else geocode now, else the Gold Coast.
        let center = site?.lat && site?.lng ? { lat: Number(site.lat), lng: Number(site.lng) }
          : client?.lat && client?.lng ? { lat: Number(client.lat), lng: Number(client.lng) } : null;
        let zoom0 = 20;
        if (!center && !site && client?.id && client?.address_street) {
          const g = await clientsService.geocode(client.id);
          if (cancelled) return;
          if (g.success) center = { lat: g.lat, lng: g.lng };
          else setBanner(`${g.error || 'Could not pin this address'} — pan and zoom to the property.`);
        }
        if (center) setHome(center);
        else { center = GOLD_COAST; zoom0 = 12; if (!site && !client?.address_street) setBanner('This client has no property address — pan and zoom to the property.'); }

        const { W, H } = wrapSize();
        mapEl.current.style.width = `${W}px`;
        mapEl.current.style.height = `${H}px`;
        mapEl.current.style.transform = 'scale(1)';
        mapEl.current.dataset.estMag = '1';
        // Stepping between letterboxes keeps the one Map instance (Google never
        // fully releases a Map, and a hundred of them in a session would add up).
        const reuse = !!mapRef.current;
        const map = reuse ? mapRef.current : new maps.Map(mapEl.current, {
          center, zoom: zoom0, mapTypeId: 'satellite', tilt: 0,
          disableDefaultUI: true, gestureHandling: 'none', keyboardShortcuts: false,
          disableDoubleClickZoom: true, clickableIcons: false, draggable: false, scrollwheel: false
        });
        if (reuse) { maps.event.trigger(map, 'resize'); map.moveCamera({ center, zoom: zoom0 }); }
        mapRef.current = map;
        setZoomState(zoom0);
        if (import.meta.env.DEV) window.__gidEstimator = { map, mag: () => magRef.current, toLatLng: (x, y) => toLatLng(x, y) }; // headless probes
        const layers = { proj: new DomLayer(), labels: new DomLayer(), handles: new DomLayer(), snap: new DomLayer() };
        for (const l of Object.values(layers)) l.setMap(map);
        layersRef.current = layers;
        if (guideRef.current) { guideRef.current.setMap(null); guideRef.current = null; }
        guideRingRef.current = [];
        const guideRing = lotRing(site?.lot);
        if (guideRing) {
          // Dashed, unfilled and labelled "Lot boundary" — like nothing a trace looks
          // like. A solid outline read as an automatic trace of the whole lot (bug: 52 Serafina).
          const ring = guideRing.map(([lng, lat]) => new maps.LatLng(Number(lat), Number(lng)));
          guideRingRef.current = ring;
          guideRef.current = new maps.Polyline({
            map, path: [...ring, ring[0]], strokeOpacity: 0, clickable: false, zIndex: 5,
            icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.95, strokeColor: GUIDE_COLOUR, strokeWeight: 2, scale: 3 }, offset: '0', repeat: '14px' }]
          });
        }
        // The rest of the drop around this lot: every other boundary faintly, traced
        // driveways in teal with the house number — so you can see what you have done
        // and where you are, without any of it being selectable.
        for (const o of contextRef.current) o.setMap(null);
        contextRef.current = []; contextLabelsRef.current = [];
        for (const c of (context?.lots || [])) {
          if (site && c.id === site.id) continue;
          const ring = lotRing(c.lot);
          if (ring) contextRef.current.push(new maps.Polygon({ map, paths: [ring.map(([lng, lat]) => new maps.LatLng(Number(lat), Number(lng)))], strokeColor: '#ffffff', strokeOpacity: 0.35, strokeWeight: 1, fillOpacity: 0, clickable: false, zIndex: 2 }));
          for (const s of c.shapes || []) {
            let g; try { g = JSON.parse(s.geojson); } catch { continue; }
            if (s.kind !== 'polygon' || !g?.coordinates?.[0]) continue;
            const rings = g.coordinates.map(openRing).filter((r) => r.length >= 3).map((r) => r.map(([lng, lat]) => new maps.LatLng(Number(lat), Number(lng))));
            if (!rings.length) continue;
            contextRef.current.push(new maps.Polygon({ map, paths: [rings[0], ...rings.slice(1).map((h) => orientHole(rings[0], h))], strokeColor: '#2DD4BF', strokeOpacity: 0.7, strokeWeight: 1, fillColor: '#2DD4BF', fillOpacity: 0.22, clickable: false, zIndex: 3 }));
          }
          if (Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng))) {
            contextLabelsRef.current.push({ position: new maps.LatLng(Number(c.lat), Number(c.lng)), text: c.traced ? `${c.label} ✓ ${fmtMeasure(c.area, 'm2')}` : c.label, className: `est-label ctx ${c.traced ? '' : 'faint'}` });
          }
        }
        if (zoomListenerRef.current) zoomListenerRef.current.remove();
        zoomListenerRef.current = map.addListener('zoom_changed', () => setZoomState(map.getZoom()));

        // How far does the imagery go here? (Google's API will not zoom past it; we magnify instead.)
        try {
          const svc = new maps.MaxZoomService();
          svc.getMaxZoomAtLatLng(center, (r) => { if (r?.status === 'OK' && r.zoom) maxZoomRef.current = r.zoom; });
        } catch { /* keep the default */ }

        // Reload saved shapes — or the autosaved working copy if it is newer
        // than the quote and differs from what was saved (Bug #388).
        let source = initial || [];
        let restored = null;
        const key = draftKey(draftId || {});
        const local = skipLocalRef.current ? null : readDraft(key);
        skipLocalRef.current = false;
        if (local && local.shapes.length) {
          const savedSig = shapesSignature((initial || []).map((m) => ({
            kind: m.kind, role: m.measure === 'exclude' ? 'exclude' : 'surface', geojson: m.geojson,
            surface_rate_id: m.surface_rate_id, soiling: m.soiling, height_m: m.height_m, width2_m: m.dim2_m, label: m.label, coats: m.coats
          })));
          const savedIso = serverTimeIso(savedAt);
          const newer = !savedIso || !local.savedAt || local.savedAt > savedIso;
          if (newer && shapesSignature(local.shapes) !== savedSig) {
            source = local.shapes.map((s) => ({ ...s, measure: s.role === 'exclude' ? 'exclude' : s.saved_measure }));
            restored = local.savedAt;
          } else if (!newer) {
            clearDraft(key); // older than the quote's own save — stale
          }
        }
        setRestoredAt(restored);
        const loaded = [];
        const bounds = new maps.LatLngBounds();
        for (const m of source) {
          let geom; try { geom = JSON.parse(m.geojson); } catch { continue; }
          const isCut = m.measure === 'exclude';
          let rings;
          if (m.kind === 'polygon') {
            rings = (geom?.coordinates || []).map(openRing).filter((r) => Array.isArray(r) && r.length >= 3);
            if (!rings.length) continue;
          } else {
            const coords = geom?.coordinates;
            if (!Array.isArray(coords) || coords.length < 2) continue;
            rings = [coords];
          }
          const latLngRings = rings.map((ring) => ring.map(([lng, lat]) => new maps.LatLng(Number(lat), Number(lng))));
          latLngRings[0].forEach((p) => bounds.extend(p));
          const surface = isCut ? null : surfaceById[String(m.surface_rate_id)];
          const key2 = `s${seq++}`;
          addOverlay(key2, m.kind, latLngRings, isCut ? EXCLUDE_COLOUR : (surface?.colour || '#2DD4BF'));
          loaded.push({
            key: key2, kind: m.kind, role: isCut ? 'exclude' : 'surface', hidden: !!m.hidden,
            label: m.label, surface_rate_id: m.surface_rate_id ? String(m.surface_rate_id) : '',
            soiling: m.soiling || 'light', coats: m.coats ?? null, height_m: m.height_m ?? null, width2_m: m.dim2_m ?? m.width2_m ?? null,
            surface_name: surface || isCut ? undefined : (m.surface_name || m.label),
            saved_rate: m.saved_rate ?? m.rate, saved_measure: isCut ? 'area' : (m.saved_measure || m.measure)
          });
        }
        await new Promise((resolve) => maps.event.addListenerOnce(map, 'idle', resolve));
        if (cancelled) return;
        // What "unchanged" means from here on: the shapes exactly as saved on the quote.
        bootSigRef.current = shapesSignature((initial || []).map((m) => ({
          kind: m.kind, role: m.measure === 'exclude' ? 'exclude' : 'surface', geojson: m.geojson,
          surface_rate_id: m.surface_rate_id, soiling: m.soiling, height_m: m.height_m, width2_m: m.dim2_m, label: m.label, coats: m.coats
        })));
        if (loaded.length) {
          shapesRef.current = loaded;
          setShapes(loaded);
        }
        if (loaded.length || guideRef.current) {
          guideRingRef.current.forEach((p) => bounds.extend(p)); // the whole lot in view, not just the traced part
          map.fitBounds(bounds, 60);
          await new Promise((resolve) => maps.event.addListenerOnce(map, 'idle', resolve));
          if (cancelled) return;
          if (map.getZoom() > maxZoomRef.current) map.setZoom(maxZoomRef.current);
          setZoomState(map.getZoom());
        }
        if (variant === 'drop' && !loaded.length && !readOnly) { modeRef.current = 'polygon'; setModeState('polygon'); } // straight into tracing
        setStatus('ready');
        bump();
      } catch (e) {
        if (!cancelled) { setStatus('error'); setError(e.message || 'Could not load the map'); }
      }
    })();

    const onPageHide = () => api.current.flushDraft?.();
    window.addEventListener('pagehide', onPageHide);

    return () => {
      cancelled = true;
      api.current.flushDraft?.(); // a remount (hot reload) or close must not lose work
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('gid-maps-auth-failed', onAuthFail);
      window.removeEventListener('resize', onResize);
      for (const [ev, fn] of Object.entries(h)) cap?.removeEventListener(ev, fn);
      for (const key of Object.keys(overlaysRef.current)) removeOverlay(key);
      if (layersRef.current) for (const l of Object.values(layersRef.current)) l.setMap(null);
      layersRef.current = null;
      clearDraftPreview();
      draftRef.current = null;
      if (guideRef.current) { guideRef.current.setMap(null); guideRef.current = null; }
      guideRingRef.current = [];
      for (const o of contextRef.current) o.setMap(null);
      contextRef.current = []; contextLabelsRef.current = [];
      if (!isOpenRef.current) { // closing: let the map go; a site change keeps it for the next boot
        if (zoomListenerRef.current) { zoomListenerRef.current.remove(); zoomListenerRef.current = null; }
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reloadKey, siteKey]);

  // Autosave: any change to the shapes (draw, edit, delete, re-label…) is
  // written to localStorage shortly after it happens.
  useEffect(() => {
    if (!isOpen || status !== 'ready' || readOnly) return undefined;
    const t = setTimeout(() => api.current.flushDraft?.(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, status, readOnly, shapes, tick]);

  // Keyboard: swallow Escape so the quote modal underneath doesn't close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      const inField = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || '');
      const fresh = api.current;
      if (e.key === 'Escape') {
        e.stopImmediatePropagation(); e.preventDefault();
        if (draftRef.current) fresh.cancelDraft();
        else if (modeRef.current !== 'select') { modeRef.current = 'select'; setModeState('select'); innerTargetRef.current = null; }
        else if (selVertexRef.current !== null) setSelVertex(null);
        else if (selectedRef.current) setSelected(null);
        else if (readOnly) onClose();
        // Never closes an editable estimator — Cancel is the only way out, and it confirms.
        return;
      }
      if (inField) return;
      if (draftRef.current) {
        if (e.key === 'Enter') { e.preventDefault(); fresh.finishDraft(); }
        else if (e.key === 'Backspace') { e.preventDefault(); fresh.popDraft(); }
        return;
      }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); fresh.zoomAt(null, null, 1); return; }
      if (e.key === '-' || e.key === '_') { e.preventDefault(); fresh.zoomAt(null, null, -1); return; }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedRef.current && !readOnly) {
        e.preventDefault();
        const key = selectedRef.current;
        const entry = overlaysRef.current[key];
        const v = selVertexRef.current;
        if (v && entry) {
          const path = pathsOf(entry)[v.p];
          const closed = isClosedRun(entry);
          const min = entry.kind === 'polygon' ? 3 : closed ? 4 : 2;
          if (path && path.getLength() > min) {
            if (closed && twinEnd(entry, v.i) != null) { path.removeAt(path.getLength() - 1); path.removeAt(0); path.push(path.getAt(0)); } // the joint goes: re-close on the next corner
            else path.removeAt(v.i);
            setSelVertex(null); bump();
          }
          else if (v.p > 0) { entry.overlay.getPaths().removeAt(v.p); setSelVertex(null); bump(); } // an inner ring at its minimum goes entirely
        } else if (e.key === 'Delete') fresh.removeShape(key);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, readOnly, onClose]);

  // ---------- derived: price every shape from its live geometry ----------
  const rows = useMemo(() => {
    void tick;
    return shapes.map((s) => {
      if (s.role === 'exclude') {
        const { raw } = measureOf(s.key, 'area', 'exclude', shapes);
        return { ...s, surface: null, measure: 'exclude', raw, gross: raw, perimeter: 0, hasInner: false, estimated: false, priced: { measured_value: 0, line_total: 0, unit_price: 0, rate: 0, soiling_factor: 1, height_m: null, unit: 'm2' }, colour: EXCLUDE_COLOUR, warning: '', note: '', unit: 'm2' };
      }
      const surface = surfaceById[s.surface_rate_id] || null;
      const measure = surface?.measure || s.saved_measure || (s.kind === 'line' ? 'length' : 'area');
      const dim = usesDim(measure) ? (Number(s.height_m) || 0) : 0;
      const dim2 = usesDim2(measure) ? (Number(s.width2_m) || 0) : 0;
      const m = measureOf(s.key, measure, 'surface', shapes, dim);
      const shape = { ...s, measure, raw_value: m.raw, rate: s.saved_rate, estimated: m.estimated, perimeter: m.perimeter };
      const priced = priceShape(shape, surface, factors);
      const colour = surface?.colour || '#2DD4BF';
      let warning = '';
      let note = '';
      if (m.invalid) warning = measure === 'ring' ? 'This surface needs an outline — draw it with Area or Rectangle, then the inner edge.' : 'Area surfaces need an outline — draw this one again with Area or Rectangle.';
      else if (!surface) warning = 'This surface was removed from the rate card — pick another.';
      else if (!(priced.rate > 0)) warning = 'No rate set for this surface yet (Settings › Rate card).';
      else if (usesDim2(measure) && !(dim + dim2 > 0)) warning = `Set the ${dimLabel(measure).toLowerCase()} and ${dimLabel2(measure).toLowerCase()} widths.`;
      else if (isLengthTimes(measure) && !usesDim2(measure) && !(dim > 0)) warning = `Set a ${dimLabel(measure).toLowerCase()}.`;
      else if (measure === 'ring' && m.noInner) warning = 'Draw the inner edge, or set a width for an estimate.';
      if (measure === 'ring' && m.estimated) note = 'Estimated from the outer edge × width — draw the inner edge for the exact band.';
      return { ...s, surface, measure, raw: m.raw, gross: m.gross, perimeter: m.perimeter, hasInner: m.hasInner, estimated: m.estimated, cutParts: m.cutParts, priced, colour, warning, note, unit: measureInfo(measure).unit };
    });
  }, [shapes, surfaceById, factors, measureOf, tick]);

  // The panel nests each cut-out under the area(s) it takes from; a cut-out
  // that overlaps nothing stays a top-level row so it can still be edited.
  const panel = useMemo(() => {
    const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));
    const nested = new Set();
    const top = [];
    for (const r of rows) {
      if (r.role === 'exclude') continue;
      const subCuts = (r.cutParts || []).map((cp) => ({ row: byKey[cp.key], area: cp.area })).filter((c) => c.row);
      subCuts.forEach((c) => nested.add(c.row.key));
      top.push({ row: r, subCuts });
    }
    for (const r of rows) if (r.role === 'exclude' && !nested.has(r.key)) top.push({ row: r, subCuts: [], orphan: true });
    // keep the original drawing order among top-level rows
    const order = Object.fromEntries(rows.map((r, i) => [r.key, i]));
    top.sort((a, b) => order[a.row.key] - order[b.row.key]);
    return top;
  }, [rows]);

  // Keep overlays + DOM layers in step with the panel (colour, selection, labels, handles).
  useEffect(() => {
    if (status !== 'ready' || !layersRef.current) return;
    const S = magRef.current;
    const dim = dimAreas || mode === 'line';
    const labels = [];
    for (const r of rows) {
      const entry = overlaysRef.current[r.key];
      if (!entry) continue;
      const selected = r.key === selectedKey;
      const isCut = r.role === 'exclude';
      const dimmed = dim && entry.kind === 'polygon' && !selected;
      entry.overlay.setVisible(!r.hidden);
      entry.overlay.setOptions({
        strokeColor: r.colour,
        strokeOpacity: dimmed ? 0.45 : 0.95,
        strokeWeight: Math.max(1.2, (selected ? 4 : 3) / S),
        zIndex: selected ? 30 : isCut ? 15 : 10,
        ...(entry.kind === 'polygon' ? { fillColor: isCut ? '#000' : r.colour, fillOpacity: dimmed ? 0.06 : isCut ? 0.35 : selected ? 0.4 : 0.3 } : {})
      });
      if (r.hidden || (dimmed && !selected)) continue;
      if (isCut && !showCutLabels && !selected) continue; // by default only the area you work with is labelled
      let text;
      if (isCut) text = `− ${fmtMeasure(r.raw, 'm2')}`;
      else if (isLengthTimes(r.measure)) text = `${fmtMeasure(r.raw, 'lm')} · ${fmtMeasure(r.priced.measured_value, 'm2')}`;
      else if (r.measure === 'ring') text = `${r.estimated ? '≈ ' : ''}${fmtMeasure(r.priced.measured_value, 'm2')}`;
      else text = fmtMeasure(r.measure === 'area' ? r.priced.measured_value : r.raw, r.unit);
      // A cut-out's label sits below its area's label instead of on top of it.
      labels.push({ position: labelPosition(entry), text, colour: r.colour, dy: isCut ? 24 : 0, className: `est-label ${selected ? 'selected' : ''} ${isCut ? 'cut' : ''}` });
    }
    if (guideRingRef.current.length && variant === 'drop') {
      // Name the dashed line once, at its southern-most corner (the hint banner sits over the top edge), so it cannot pass for a trace.
      const bottom = guideRingRef.current.reduce((a, b) => (b.lat() < a.lat() ? b : a));
      labels.push({ position: bottom, text: site?.title ? `${site.title} — this one` : 'This house', className: 'est-label guide', dy: 14 });
    }
    for (const l of contextLabelsRef.current) labels.push(l);
    layersRef.current.labels.setItems(labels, S);

    if (draftRef.current) return; // draft dots own the handles layer while drawing
    const handles = [];
    const entry = selectedKey && overlaysRef.current[selectedKey];
    if (entry && !readOnly) {
      const colour = rows.find((r) => r.key === selectedKey)?.colour || '#2DD4BF';
      pathsOf(entry).forEach((path, p) => {
        const pts = pathToLatLngs(path);
        const n = pts.length;
        const segs = entry.kind === 'polygon' ? n : n - 1;
        for (let i = 0; i < segs; i++) handles.push({ position: mapsRef.current.geometry.spherical.interpolate(pts[i], pts[(i + 1) % n], 0.5), className: 'est-handle mid', colour });
        pts.forEach((pt, i) => handles.push({ position: pt, className: `est-handle ${selVertex && selVertex.p === p && selVertex.i === i ? 'active' : ''}`, colour }));
      });
    }
    layersRef.current.handles.setItems(handles, S);
  }, [rows, selectedKey, selVertex, status, readOnly, dimAreas, showCutLabels, mode, mag, tick]);

  const setShape = (key, patch) => setShapes((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  function changeSurface(key, id) {
    const surface = surfaceById[id];
    setShapes((prev) => prev.map((s) => {
      if (s.key !== key) return s;
      const wasDefault = !s.label || s.label === (surfaceById[s.surface_rate_id]?.name || s.surface_name);
      return {
        ...s, surface_rate_id: id,
        ...factorFields(surface, { trade: shapeTrade(s), soiling: s.soiling, coats: s.coats }),
        label: wasDefault ? (surface?.name || s.label) : s.label,
        height_m: usesDim(surface?.measure) ? (s.height_m ?? defaultDim(surface)) : s.height_m,
        width2_m: usesDim2(surface?.measure) ? (s.width2_m ?? defaultDim2(surface)) : s.width2_m
      };
    }));
  }
  function removeShape(key) {
    removeOverlay(key);
    setShapes((prev) => prev.filter((s) => s.key !== key));
    if (selectedRef.current === key) setSelected(null);
    bump();
  }
  function duplicateTo(key, surfaceId) {
    const entry = overlaysRef.current[key]; const src = shapes.find((s) => s.key === key);
    const surface = surfaceById[surfaceId];
    if (!entry || !src || !surface) return;
    const newKey = `s${seq++}`;
    addOverlay(newKey, entry.kind, pathsOf(entry).map(pathToLatLngs), surface.colour);
    setShapes((prev) => [...prev, {
      key: newKey, kind: entry.kind, role: 'surface', hidden: false, label: surface.name, surface_rate_id: String(surface.id),
      ...factorFields(surface, { trade: shapeTrade(src), soiling: src.soiling, coats: src.coats }), height_m: defaultDim(surface), width2_m: defaultDim2(surface)
    }]);
    setSelected(newKey);
    bump();
  }
  function zoomTo(key) {
    const entry = overlaysRef.current[key]; const maps = mapsRef.current; const map = mapRef.current;
    if (!entry || !map) return;
    if (magRef.current !== 1) applyMag(1);
    const b = new maps.LatLngBounds();
    pathsOf(entry)[0].forEach((p) => b.extend(p));
    map.fitBounds(b, 80);
    maps.event.addListenerOnce(map, 'idle', () => { if (map.getZoom() > maxZoomRef.current) map.setZoom(maxZoomRef.current); setZoomState(map.getZoom()); });
  }
  function recentre() {
    const map = mapRef.current; if (!map || !home) return;
    if (magRef.current !== 1) applyMag(1);
    map.moveCamera({ center: home, zoom: Math.min(20, maxZoomRef.current) });
    setZoomState(map.getZoom());
  }

  const priced = rows.filter((r) => r.role !== 'exclude');
  const subtotal = priced.reduce((s, r) => s + (r.warning ? 0 : r.priced.line_total), 0);
  const minCharge = Number(settings.min_charge) || 0;
  const blockers = priced.filter((r) => r.warning && !r.warning.startsWith('No rate')).length;

  const unsaved = status === 'ready' && !readOnly && shapesSignature(snapshotShapes(shapes)) !== bootSigRef.current;
  function requestClose() {
    if (unsaved && !window.confirm(`Close the map without applying? The ${rows.length} marked area${rows.length === 1 ? '' : 's'} are kept and will be restored next time you open it.`)) return;
    flushDraft();
    onClose();
  }
  function discardRestored() {
    if (!window.confirm('Discard the restored areas and go back to what is saved on the quote?')) return;
    clearDraft(currentKey());
    skipLocalRef.current = true;
    setReloadKey((k) => k + 1);
  }

  function apply() {
    if (draftRef.current) cancelDraft(); // an unfinished outline is not a shape
    const data = rows.map((r) => {
      const entry = overlaysRef.current[r.key];
      return {
        key: r.key, kind: r.kind, role: r.role, label: r.label, surface_rate_id: r.surface_rate_id ? Number(r.surface_rate_id) : null,
        soiling: r.soiling, coats: r.coats ?? null, height_m: r.priced.height_m, width2_m: r.priced.width2_m, measure: r.measure, raw_value: r.raw, rate: r.saved_rate,
        estimated: r.estimated, perimeter: r.perimeter,
        surface_name: r.surface?.name || r.surface_name, geojson: geojsonOf(entry)
      };
    });
    onApply?.(buildQuoteLines(data, surfaces, settings, { groupBySurface, priceLines }));
    if (variant === 'drop') { // the page saves straight away, so nothing is unsaved from here
      bootSigRef.current = shapesSignature(snapshotShapes());
      clearDraft(currentKey());
    }
  }
  /** Prev / Next letterbox: an unsaved outline is kept as a draft and comes back. */
  function stepTo(fn) {
    if (!fn) return;
    if (draftRef.current) cancelDraft();
    if (unsaved && !window.confirm('Move on without saving this one? The outline is kept as a draft and comes back when you return to it.')) return;
    flushDraft();
    fn();
  }

  if (!isOpen) return null;

  const address = client ? [client.address_street, client.address_suburb, client.address_state, client.address_postcode].filter(Boolean).join(', ') : '';
  const drawing = draftCount >= 0;
  const innerTarget = mode === 'inner' ? rows.find((r) => r.key === innerTargetRef.current) : null;
  const hint = readOnly ? 'Viewing the areas marked for this quote. Drag to pan, scroll to zoom.'
    : variant === 'drop' && rows.length === 0 && mode === 'polygon' ? 'Trace the driveway: click around its edge, garage to kerb. The dashed yellow line only shows which house you are on — it is not a trace, and a driveway can run over it. Double-click, Enter, or click the first point to finish.'
      : variant === 'drop' && rows.length === 0 && mode === 'select' ? 'Nothing traced yet. Pick Area or Rectangle and outline the driveway — the dashed yellow line only shows which house you are on.'
    : mode === 'inner' ? `Now click around the inner edge of ${innerTarget?.label || 'the outline'} (where the eaves meet the walls). Double-click, Enter, or click the first point to finish.`
      : mode === 'polygon' ? 'Click around the edge of the surface. Points snap to nearby corners and edges. Double-click, Enter, or click the first point to finish.'
        : mode === 'rectangle' ? 'Click one corner, then the opposite corner.'
          : mode === 'line' ? 'Click along the run (gutters, walls, fence) — areas are dimmed while you trace. Click the first point again to go right round, or double-click / Enter to finish.'
            : mode === 'exclude' ? 'Outline what to leave out (pool, garden bed). It is subtracted from every area it overlaps.'
              : 'Click a shape to select it, then drag its corners (drag a midpoint to add one). Scroll to zoom — past the imagery limit the map magnifies.';
  const anyHidden = shapes.some((s) => s.hidden);

  return createPortal(
    <div className="est-root" role="dialog" aria-modal="true" aria-label="Measure from map">
      <header className="est-header">
        <div className="est-title">
          <strong>{readOnly ? 'Marked areas' : variant === 'drop' ? 'Trace the driveway' : 'Measure from map'}</strong>
          <span className="text-muted" title={site ? `${site.title || ''} · ${site.subtitle || ''}` : address}>{site ? site.title : client?.name}{(site ? site.subtitle : address) ? ` · ${site ? site.subtitle : address}` : ''}</span>
        </div>
        {!readOnly && (
          <div className="est-tools" role="toolbar" aria-label="Drawing tools">
            <button type="button" className={`est-tool ${mode === 'select' ? 'active' : ''}`} onClick={() => setMode('select')} title="Select / edit"><CursorIcon /> Select</button>
            <button type="button" className={`est-tool ${mode === 'polygon' ? 'active' : ''}`} onClick={() => setMode('polygon')} title="Outline an area"><AreaIcon /> Area</button>
            <button type="button" className={`est-tool ${mode === 'rectangle' ? 'active' : ''}`} onClick={() => setMode('rectangle')} title="Quick rectangle"><RectIcon /> Rectangle</button>
            <button type="button" className={`est-tool ${mode === 'line' ? 'active' : ''}`} onClick={() => setMode('line')} title="Trace a run (gutters, walls, fence)"><LineIcon /> Line</button>
            <button type="button" className={`est-tool cut ${mode === 'exclude' ? 'active' : ''}`} onClick={() => setMode('exclude')} title="Cut a pool, garden bed or shed out of the areas it sits in"><CutIcon /> Cut-out</button>
            <div className="est-defaults">
              {hasPaint && (
                <>
                  <label htmlFor="est_trade">Work</label>
                  <select id="est_trade" value={trade} onChange={(e) => setTrade(e.target.value)} title="Which surfaces the picker offers — pressure washing or painting">
                    {TRADES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </>
              )}
              <label htmlFor="est_next_surface">Surface</label>
              <select id="est_next_surface" value={nextSurface} onChange={(e) => setNextSurface(e.target.value)} title={companionOf(nextSurface) ? 'One outline gives both surfaces' : measureInfo(primaryOf(nextSurface)?.measure).hint}>
                {pickerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <label htmlFor="est_next_soiling">{trade === 'paint' ? 'Condition' : 'Soiling'}</label>
              <select id="est_next_soiling" value={nextSoiling} onChange={(e) => setNextSoiling(e.target.value)} title={trade === 'paint' ? 'Multiplies the rate for the prep a tired surface needs' : undefined}>
                {factorOptions(trade).map((s) => <option key={s.value} value={s.value}>{s.label} ×{factors[s.value]}</option>)}
              </select>
              {trade === 'paint' && (
                <>
                  <label htmlFor="est_next_coats">Coats</label>
                  <select id="est_next_coats" value={nextCoats} onChange={(e) => setNextCoats(Number(e.target.value))}>
                    {COATS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </>
              )}
            </div>
          </div>
        )}
        <div className="est-header-actions">
          {step && (
            <div className="est-step" role="group" aria-label="Step through the letterboxes">
              <button type="button" className="btn btn-sm btn-outline" onClick={() => stepTo(step.onPrev)} disabled={!step.canPrev} title="Previous letterbox">‹ Prev</button>
              <span>{step.label}</span>
              <button type="button" className="btn btn-sm btn-outline" onClick={() => stepTo(step.onNext)} disabled={!step.canNext} title="Next letterbox">Next ›</button>
              {step.onSkip && <button type="button" className="btn btn-sm btn-outline" onClick={() => stepTo(step.onSkip)} title="No driveway worth tracing here — mark it skipped and move on">Skip</button>}
            </div>
          )}
          <button type="button" className="btn btn-sm btn-outline" onClick={recentre} disabled={!home} title="Back to the property pin">Re-centre</button>
          <button type="button" className="btn btn-sm btn-outline" onClick={readOnly ? onClose : requestClose}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button type="button" className="btn btn-sm btn-primary" onClick={apply} disabled={status !== 'ready' || priced.length === 0 || (priceLines && blockers > 0) || drawing}
              title={priceLines && blockers ? 'Fix the flagged shapes first' : variant === 'drop' ? 'Save the traced surfaces for this letterbox, priced at the rate card' : priceLines ? 'Turn these areas into quote lines' : 'Save the areas on the quote without pricing them'}>
              {applyLabel || (priceLines ? 'Apply to quote' : 'Save areas only')}{priced.length && variant !== 'drop' ? ` (${priced.length})` : ''}
            </button>
          )}
        </div>
      </header>

      <div className="est-body">
        <div className="est-map-wrap" ref={wrapEl}>
          <div ref={mapEl} className="est-map" />
          <div ref={captureEl} className={`est-capture ${mode !== 'select' && !readOnly ? 'drawing' : ''} ${cursor}`} />
          <div className="est-zoom" aria-label="Zoom">
            <button type="button" onClick={() => zoomAt(null, null, 1)} title="Zoom in (+)">+</button>
            <button type="button" onClick={() => zoomAt(null, null, -1)} title="Zoom out (−)">−</button>
            <span className="est-zoom-badge" title="Map zoom level · magnification past the imagery limit">z{zoom}{mag > 1 ? ` · ${mag}×` : ''}</span>
          </div>
          {status === 'loading' && <div className="est-overlay-msg">Loading satellite imagery…</div>}
          {status === 'error' && <div className="est-overlay-msg est-error">{error}</div>}
          {status === 'ready' && restoredAt && !readOnly && (
            <div className="est-banner restore">
              Restored your unsaved areas from {new Date(restoredAt).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}.
              <button type="button" className="est-mini" onClick={discardRestored}>Discard, use saved</button>
            </div>
          )}
          {status === 'ready' && banner && <div className={`est-banner warn ${restoredAt ? 'second' : ''}`}>{banner}</div>}
          {status === 'ready' && !banner && !drawing && !restoredAt && <div className={`est-banner ${mode === 'inner' ? 'inner' : ''}`}>{hint}</div>}
          {drawing && (
            <div className="est-draft-bar">
              <span>{mode === 'rectangle' ? 'Now click the opposite corner' : `${mode === 'inner' ? 'Inner edge: ' : ''}${draftCount} point${draftCount === 1 ? '' : 's'} · Enter or double-click to finish · Backspace undoes · Esc cancels`}</span>
              {mode !== 'rectangle' && <button type="button" className="btn btn-primary" onClick={finishDraft} disabled={draftCount < (mode === 'line' ? 2 : 3)}>Finish</button>}
              <button type="button" className="btn btn-outline" onClick={cancelDraft}>Cancel</button>
            </div>
          )}
          <div className="est-attrib">Measured flat (top-down). Imagery can be a year or two old — check against the site.</div>
        </div>

        <aside className="est-panel">
          <div className="est-panel-head">
            <strong>{variant === 'drop' ? 'Traced' : 'Marked areas'} ({priced.length}{rows.length !== priced.length ? ` + ${rows.length - priced.length} cut-out` : ''})</strong>
            {!readOnly && rows.length > 0 && (
              <div className="est-panel-tools">
                <button type="button" className={`est-mini ${dimAreas ? 'active' : ''}`} onClick={() => setDimAreas((v) => !v)} title="Fade the filled areas so you can see the roofline and fences underneath">{dimAreas ? 'Areas faded' : 'Fade areas'}</button>
                {rows.some((r) => r.role === 'exclude') && (
                  <button type="button" className={`est-mini ${showCutLabels ? 'active' : ''}`} onClick={() => setShowCutLabels((v) => !v)} title="Show the cut-out sizes on the map as well as the area you work with">{showCutLabels ? 'Both sizes' : 'Area only'}</button>
                )}
                {anyHidden && <button type="button" className="est-mini" onClick={() => setShapes((prev) => prev.map((s) => ({ ...s, hidden: false })))}>Show all</button>}
              </div>
            )}
          </div>
          {!readOnly && priced.length > 0 && variant !== 'drop' && (
            <div className="est-apply-options">
              <label className="checkbox-label est-group-toggle" title="Off = the shapes are saved on the quote for the areas list and map, but you write the price yourself">
                <input type="checkbox" checked={priceLines} onChange={(e) => setPriceLines(e.target.checked)} /> Add a priced line per area
              </label>
              {priceLines && priced.length > 1 && (
                <label className="checkbox-label est-group-toggle">
                  <input type="checkbox" checked={groupBySurface} onChange={(e) => setGroupBySurface(e.target.checked)} /> One quote line per surface
                </label>
              )}
            </div>
          )}

          {rows.length === 0 ? (
            <div className="est-panel-empty text-muted">
              {readOnly ? 'No areas were marked on this quote.' : variant === 'drop' ? (
                <>
                  <p>Nothing traced yet. The dashed yellow line only shows which house you are on — it is not a trace and cannot be edited.</p>
                  <ol>
                    <li>With <strong>Area</strong> (or <strong>Rectangle</strong>) click around the driveway — usually the slab at the front, garage to kerb. Points snap to corners you have drawn. Trace whichever house you like: it is saved under the lot it sits on.</li>
                    <li>Add a path or patio the same way if it should be priced too.</li>
                    <li><strong>Save driveway</strong> — it steps to the next letterbox.</li>
                  </ol>
                </>
              ) : (
                <>
                  <p>Nothing marked yet. Each shape becomes a priced line on the quote.</p>
                  <ol>
                    <li>{hasPaint ? <>Choose <strong>Washing</strong> or <strong>Painting</strong>, the surface, and how dirty it is — for painting, its condition and the coats.</> : 'Choose the surface and how dirty it is.'}</li>
                    <li>Pick <strong>Area</strong>, <strong>Rectangle</strong> or <strong>Line</strong> and click it out on the map. Points snap to corners you have already drawn.</li>
                    <li>For <strong>eaves</strong>, outline the roof edge, then the wall line — the band between is the area. Pick <strong>Eaves + Gutters</strong> to get both from the one outline.</li>
                    <li>Use <strong>Cut-out</strong> for a pool or garden bed inside an area.</li>
                    <li>Adjust the corners, then <strong>Apply to quote</strong>.</li>
                  </ol>
                </>
              )}
            </div>
          ) : (
            <div className="est-panel-list">
              {panel.map(({ row: r, subCuts }) => (
                <div key={r.key} className={`est-shape ${r.key === selectedKey ? 'selected' : ''} ${r.role === 'exclude' ? 'excluded' : ''} ${r.hidden ? 'hidden' : ''}`} onClick={() => !readOnly && setSelected(r.key)}>
                  <div className="est-shape-head">
                    <span className="est-swatch" style={{ background: r.colour }} />
                    {readOnly
                      ? <strong style={{ flex: 1 }}>{r.label}</strong>
                      : <input aria-label="Label" value={r.label} onChange={(e) => setShape(r.key, { label: e.target.value })} onClick={(e) => e.stopPropagation()} placeholder={r.surface?.name || 'Label'} />}
                    <span className="est-shape-raw">
                      {r.role === 'exclude' ? `− ${fmtMeasure(r.raw, 'm2')}`
                        : r.measure === 'ring' ? `${r.estimated ? '≈ ' : ''}${fmtMeasure(r.raw, 'm2')}`
                          : fmtMeasure(r.kind === 'polygon' && r.measure === 'area' ? r.gross : r.raw, r.kind === 'polygon' && r.measure === 'area' ? 'm2' : 'lm')}
                    </span>
                    {!readOnly && <button type="button" className="est-icon-btn" aria-label={r.hidden ? 'Show shape' : 'Hide shape'} title={r.hidden ? 'Show on the map' : 'Hide on the map (still counted)'} onClick={(e) => { e.stopPropagation(); setShape(r.key, { hidden: !r.hidden }); }}><EyeIcon off={r.hidden} /></button>}
                    {!readOnly && <button type="button" className="est-shape-remove" aria-label="Remove shape" onClick={(e) => { e.stopPropagation(); removeShape(r.key); }}>×</button>}
                  </div>
                  {r.role === 'exclude' ? (
                    <div className="est-shape-price">Cut-out — not inside any area yet; move it or draw the area it belongs to.</div>
                  ) : readOnly ? (
                    <div className="est-readonly-row text-muted">
                      <span>{r.surface?.name || r.surface_name}{r.coats != null ? ` · ${r.coats} coat${r.coats === 1 ? '' : 's'}${r.soiling !== 'good' ? `, ${r.soiling} condition` : ''}` : r.soiling !== 'light' ? ` · ${r.soiling} soiling` : ''}{usesDim2(r.measure) ? ` · ${fmtDim(r.priced.height_m || 0)} + ${fmtDim(r.priced.width2_m || 0)} m` : usesDim(r.measure) && r.priced.height_m ? ` · ${fmtDim(r.priced.height_m)} m ${dimLabel(r.measure).toLowerCase()}` : ''}</span>
                      <span>{fmtMeasure(r.priced.measured_value, r.unit)}</span>
                    </div>
                  ) : (
                    <>
                      <div className="est-shape-fields" onClick={(e) => e.stopPropagation()}>
                        <select aria-label="Surface" value={r.surface_rate_id} onChange={(e) => changeSurface(r.key, e.target.value)}>
                          {!r.surface && <option value="">{r.surface_name ? `${r.surface_name} (removed)` : 'Choose a surface…'}</option>}
                          {surfaceOptions(shapeSurfaces)}
                        </select>
                        <select aria-label={r.coats != null ? 'Condition' : 'Soiling'} value={r.soiling} onChange={(e) => setShape(r.key, { soiling: e.target.value })}>
                          {factorOptions(shapeTrade(r)).map((s) => <option key={s.value} value={s.value}>{s.label} ×{factors[s.value]}</option>)}
                        </select>
                        {r.coats != null && (
                          <select aria-label="Coats" value={r.coats} onChange={(e) => setShape(r.key, { coats: Number(e.target.value) })}>
                            {COATS.map((n) => <option key={n} value={n}>{n} coat{n === 1 ? '' : 's'}</option>)}
                          </select>
                        )}
                        {usesDim(r.measure) && (
                          <label className="est-height">{dimLabel(r.measure)}
                            <input aria-label={`${dimLabel(r.measure)} in metres`} type="number" min="0" step={usesDim2(r.measure) ? '0.05' : '0.1'} value={r.height_m ?? ''} onChange={(e) => setShape(r.key, { height_m: e.target.value })} /> m
                          </label>
                        )}
                        {usesDim2(r.measure) && (
                          <label className="est-height">{dimLabel2(r.measure)}
                            <input aria-label={`${dimLabel2(r.measure)} in metres`} type="number" min="0" step="0.05" value={r.width2_m ?? ''} onChange={(e) => setShape(r.key, { width2_m: e.target.value })} /> m
                          </label>
                        )}
                      </div>
                      {r.warning
                        ? <div className="est-shape-warn">{r.warning}</div>
                        : (
                          <div className="est-shape-price">
                            {r.measure === 'area' && r.gross > r.raw + 0.05 ? <span className="text-muted">{fmtMeasure(r.gross, 'm2')} − {fmtMeasure(r.gross - r.raw, 'm2')} cut out = </span> : null}
                            {r.measure === 'ring' && r.estimated ? <span className="text-muted">≈ {fmtMeasure(r.perimeter, 'lm')} × {fmtDim(r.priced.height_m || 0)} m = </span> : null}
                            {r.measure === 'ring' && r.hasInner ? <span className="text-muted">{fmtMeasure(r.gross, 'm2')} outer less inner = </span> : null}
                            {usesDim2(r.measure) ? <span className="text-muted">{fmtMeasure(r.raw, 'lm')} × ({fmtDim(r.priced.height_m || 0)} + {fmtDim(r.priced.width2_m || 0)}) m = </span> : null}
                            {fmtMeasure(r.priced.measured_value, r.unit)} × {formatMoney(r.priced.unit_price)}
                            {r.priced.soiling_factor !== 1 || r.priced.coats > 1
                              ? ` (${[formatMoney(r.priced.rate), r.priced.coats > 1 ? `${r.priced.coats} coats` : null, r.priced.soiling_factor !== 1 ? r.priced.soiling_factor : null].filter(Boolean).join(' × ')})`
                              : ''} = <strong>{formatMoney(r.priced.line_total)}</strong>
                          </div>
                        )}
                      {r.note && !r.warning && <div className="est-shape-note text-muted">{r.note}</div>}
                      <div className="est-shape-actions" onClick={(e) => e.stopPropagation()}>
                        {r.measure === 'ring' && r.kind === 'polygon' && (
                          r.hasInner
                            ? <><button type="button" className="btn btn-sm btn-outline" onClick={() => startInnerDraft(r.key)}>Redraw inner edge</button><button type="button" className="btn btn-sm btn-outline" onClick={() => removeInner(r.key)}>Remove inner</button></>
                            : <button type="button" className="btn btn-sm btn-primary" onClick={() => startInnerDraft(r.key)}>Draw inner edge</button>
                        )}
                        <select aria-label="Copy this shape to another surface" value="" onChange={(e) => { if (e.target.value) duplicateTo(r.key, e.target.value); }}>
                          <option value="">Copy shape to…</option>
                          {surfaceOptions(shapeSurfaces.filter((s) => String(s.id) !== r.surface_rate_id))}
                        </select>
                        <button type="button" className="btn btn-sm btn-outline" onClick={() => zoomTo(r.key)}>Zoom</button>
                      </div>
                    </>
                  )}
                  {subCuts.length > 0 && (
                    <div className="est-subcuts">
                      {subCuts.map(({ row: c, area }) => (
                        <div key={c.key} className={`est-subcut ${c.key === selectedKey ? 'selected' : ''} ${c.hidden ? 'hidden' : ''}`} onClick={(e) => { e.stopPropagation(); if (!readOnly) setSelected(c.key); }}>
                          <span className="est-swatch" style={{ background: c.colour }} />
                          {readOnly
                            ? <span className="est-subcut-label">{c.label}</span>
                            : <input aria-label="Cut-out label" value={c.label} onChange={(e) => setShape(c.key, { label: e.target.value })} onClick={(e) => e.stopPropagation()} placeholder="Cut-out" />}
                          <span className="est-shape-raw">− {fmtMeasure(area, 'm2')}</span>
                          {!readOnly && <button type="button" className="est-icon-btn" aria-label={c.hidden ? 'Show cut-out' : 'Hide cut-out'} title={c.hidden ? 'Show on the map' : 'Hide on the map (still subtracted)'} onClick={(e) => { e.stopPropagation(); setShape(c.key, { hidden: !c.hidden }); }}><EyeIcon off={c.hidden} /></button>}
                          {!readOnly && <button type="button" className="est-shape-remove" aria-label="Remove cut-out" onClick={(e) => { e.stopPropagation(); removeShape(c.key); }}>×</button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="est-panel-foot">
            <div className="est-subtotal"><span>Measured total{settings.gst_registered !== 'false' ? ' (inc GST)' : ''}</span><strong>{formatMoney(subtotal)}</strong></div>
            {minCharge > 0 && subtotal > 0 && subtotal < minCharge && !readOnly && (
              <div className="est-note warn">Under the {formatMoney(minCharge)} minimum — a top-up line will bring the quote to the minimum.</div>
            )}
            {!readOnly && priceLines && priced.some((r) => r.warning?.startsWith('No rate')) && (
              <div className="est-note warn">Some surfaces have no rate yet — those lines will come through at $0 until you set rates under Settings › Rate card.</div>
            )}
            {!readOnly && <div className="est-note">{variant === 'drop' ? 'Saved per letterbox, priced at the rate card. When they book, these become the quote\'s map lines.' : priceLines ? 'Lines stay editable on the quote after you apply.' : 'No lines will be added — the areas can still be listed on the quote and shown on the map.'}</div>}
          </div>
        </aside>
      </div>
    </div>,
    document.body
  );
}
